import React, { useEffect, useState } from "react";

const API = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8090";

const QUESTIONS = {
  student: [["learning_experience", "Learning experience"], ["support", "Support from school"], ["safety_belonging", "Safety and belonging"]],
  parent: [["communication", "School communication"], ["progress_visibility", "Progress visibility"], ["value_for_fees", "Value for fees"]],
  teacher: [["workplace_support", "Workplace support"], ["teaching_resources", "Teaching resources"], ["professional_growth", "Professional growth"]],
  administration: [["operational_readiness", "Operational readiness"], ["staff_coordination", "Staff coordination"], ["resource_availability", "Resource availability"]],
};

export default function BeneficiaryFeedbackCard() {
  const [data, setData] = useState(null);
  const [ratings, setRatings] = useState({});
  const [comment, setComment] = useState("");
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    fetch(`${API}/beneficiary-feedback/me`, { headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` } })
      .then((response) => {
        if (response.status === 403) { setHidden(true); return Promise.reject(new Error("__hidden__")); }
        return response.ok ? response.json() : Promise.reject(new Error("Feedback is unavailable right now."));
      })
      .then((result) => {
        setData(result);
        setRatings(result.response?.ratings || {});
        setComment(result.response?.comment || "");
      })
      .catch((error) => { if (error.message !== "__hidden__") setMessage(error.message); });
  }, []);

  if (hidden) return null;
  if (!data) {
    return message
      ? <section className="rounded-2xl border border-rose-200 bg-rose-50 p-5 text-sm text-rose-700">{message}</section>
      : <section className="rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-500">Loading feedback form...</section>;
  }

  const questions = QUESTIONS[data.group] || [];
  const save = async (event) => {
    event.preventDefault();
    if (questions.some(([key]) => !ratings[key])) return setMessage("Please select all three ratings.");
    setSaving(true);
    setMessage("");
    try {
      const response = await fetch(`${API}/beneficiary-feedback/me`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("accessToken")}` },
        body: JSON.stringify({ ratings, comment }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.detail || "Unable to save feedback.");
      setData((previous) => ({ ...previous, response: result }));
      setMessage("Feedback saved.");
    } catch (error) {
      setMessage(error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-2xl border border-orange-100 bg-white p-6 shadow-sm font-[Poppins]">
      <h2 className="text-xl font-bold text-slate-900">School experience feedback</h2>
      <p className="mt-1 text-sm text-slate-600">Your aggregate-only feedback helps the school improve.</p>
      <form onSubmit={save} className="mt-5 space-y-4">
        {questions.map(([key, label]) => (
          <fieldset key={key}>
            <legend className="text-sm font-semibold text-slate-700">{label}</legend>
            <div className="mt-2 flex gap-2">
              {[1, 2, 3, 4, 5].map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setRatings((previous) => ({ ...previous, [key]: value }))}
                  className={`h-9 w-9 rounded-full text-sm font-bold ${ratings[key] === value ? "bg-[#D67C2C] text-white" : "bg-orange-50 text-[#D67C2C] hover:bg-orange-100"}`}
                >
                  {value}
                </button>
              ))}
            </div>
          </fieldset>
        ))}
        <textarea value={comment} maxLength="1000" onChange={(event) => setComment(event.target.value)} className="w-full rounded-xl border border-slate-200 p-3 text-sm focus:border-[#D67C2C] focus:outline-none" rows="3" placeholder="Optional comment" />
        {message && <p className={message === "Feedback saved." ? "text-sm text-emerald-700" : "text-sm text-rose-700"}>{message}</p>}
        <button disabled={saving} className="rounded-xl bg-[#D67C2C] px-5 py-3 text-sm font-semibold text-white disabled:opacity-60">{saving ? "Saving..." : data.response ? "Update feedback" : "Submit feedback"}</button>
      </form>
    </section>
  );
}
